/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { type DefaultValues } from 'react-hook-form';

import { type IntakeForm } from '../../lib/api/intake.js';

import type { BadgeTone } from '../../components/ui/badge.js';
import type { IntakeStatus, IntakeSubmitInput } from '@therapy/shared-validators';

const EMPTY_GUARDIAN = { name: '', relationship: '', contact: '', email: '' } as const;

export const INTAKE_CARD_CLASS =
  'rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-900';

/** Badge tone per intake status (the admin client list + review header). */
export const INTAKE_STATUS_TONE: Readonly<Record<IntakeStatus, BadgeTone>> = {
  form_pending: 'slate',
  form_filled: 'violet',
  under_review: 'amber',
  scheduled: 'violet',
  arrived: 'green',
  completed: 'green',
};

/**
 * Merge the loaded form with empty scaffolds so every RHF control is controlled
 * from first render (avoids the uncontrolled→controlled warning). Shared by the
 * parent submit form and the admin review panel.
 */
export function toIntakeDefaults(form: IntakeForm): DefaultValues<IntakeSubmitInput> {
  return {
    basicDetails: { guardians: [{ ...EMPTY_GUARDIAN }], ...(form.basicDetails ?? {}) },
    birthHistory: form.birthHistory ?? {},
    postNatal: form.postNatal ?? {},
    milestones: form.milestones ?? {},
    motorSkills: { gross: {}, fine: {}, safety: {}, ...(form.motorSkills ?? {}) },
    dailyLiving: { foodTextures: [], ...(form.dailyLiving ?? {}) },
    speechLanguage: { communicationMethods: [], ...(form.speechLanguage ?? {}) },
    preferencesGoals: form.preferencesGoals ?? {},
  } as DefaultValues<IntakeSubmitInput>;
}
